<template>
  <div class="target-tree">
    <div class="target-tree__toolbar">
      <el-input
        v-model="keyword"
        placeholder="搜索电站名称"
        clearable
        size="small"
        class="tt-search"
      >
        <template #prefix><el-icon><Search /></el-icon></template>
      </el-input>
    </div>

    <div class="target-tree__body" v-loading="loading">
      <el-tree
        ref="treeRef"
        :data="treeData"
        show-checkbox
        node-key="id"
        lazy
        :load="loadNode"
        :props="treeProps"
        :expand-on-click-node="false"
        :check-strictly="false"
        class="tt-tree"
        @check="onCheck"
      >
        <template #default="{ node, data }">
          <span class="tt-node" :class="`tt-node--${data.level}`">
            <el-icon class="tt-node__icon">
              <OfficeBuilding v-if="data.level === 'station'" />
              <Cpu v-else />
            </el-icon>
            <span class="tt-node__label" :title="data.name">{{ data.name }}</span>
            <el-tag v-if="data.level === 'station' && data.isPv" size="small" type="success" effect="light" class="tt-node__pv" round>
              光伏
            </el-tag>
            <span v-if="data.level === 'station' && data.countText" class="tt-node__count">{{ data.countText }}</span>
          </span>
        </template>
      </el-tree>

      <div v-if="!loading && !treeData.length" class="tt-empty">
        <el-empty description="暂无可下发的电站" :image-size="80" />
      </div>

      <div v-if="hasMore" class="tt-more">
        <el-button text type="primary" size="small" :loading="loadingMore" @click="loadMoreStations">
          加载更多电站（已加载 {{ treeData.length }}/{{ totalStations }}）
        </el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, watch } from 'vue'
import { Search } from '@element-plus/icons-vue'
import { OfficeBuilding, Cpu } from '@element-plus/icons-vue'
import { getOwnerStationsPage, elinks } from '../api.js'
import useAppStore from '@/store/modules/app'
import { debounce } from '@/utils'

const emit = defineEmits(['change'])

const appStore = useAppStore()

/**
 * 判断电站是否接入光伏。
 * 优先取列表项自带字段（pvAccess / pv_access），回退到 app store 的全局电站列表（queryNameList，含 pvAccess）。
 * 光伏与削峰填谷的策略语义不同,勾选前提示便于识别。
 */
function isPvStation(st, stationId) {
  const raw = st?.pvAccess ?? st?.pv_access
  if (raw != null) return !!(Number(raw))
  const s = appStore.getStationById?.(stationId)
  return !!(Number(s?.pvAccess ?? s?.pv_access ?? 0))
}

const treeRef = ref(null)
const loading = ref(false)
const loadingMore = ref(false)
const keyword = ref('')
const treeData = ref([])

const pageNum = ref(1)
const pageSize = 30
const totalStations = ref(0)
const hasMore = ref(false)

const treeProps = {
  children: 'children',
  label: 'name',
  isLeaf: 'isLeaf',
  // 无 elink 设备的电站行隐藏勾选框
  class: (data) => (data.level === 'station' && data.noDevice ? 'tt-node--no-device' : ''),
}

/** 拉取一页电站作为顶层节点（懒加载，不预取 eLink） */
async function fetchStationsPage(reset = false) {
  const res = await getOwnerStationsPage({
    pageNum: pageNum.value,
    pageSize,
    name: keyword.value.trim() || undefined,
  })
  const list = res?.data?.list || []
  totalStations.value = res?.data?.total || list.length
  const nodes = list.map((st) => {
    const stationId = st.stationId ?? st.id
    return {
      id: `station:${stationId}`,
      level: 'station',
      name: st.name || `电站${stationId}`,
      station_id: stationId,
      station_name: st.name,
      isPv: isPvStation(st, stationId),
      isLeaf: false,
    }
  })
  if (reset) {
    treeData.value = nodes
  } else {
    // 按节点 id 去重，避免后端跨页返回重复电站或重复翻页导致的重复展示
    const existIds = new Set(treeData.value.map((n) => n.id))
    treeData.value.push(...nodes.filter((n) => !existIds.has(n.id)))
  }
  hasMore.value = treeData.value.length < totalStations.value
}

/** el-tree 懒加载：只用于电站节点按需拉取其 eLink，根节点直接用 :data 绑定 */
async function loadNode(node, resolve) {
  // 电站节点：懒加载 eLink
  if (node.data?.level === 'station') {
    const stationId = node.data.station_id
    try {
      const elkRes = await elinks(stationId)
      const elinkList = elkRes?.data || []
      const children = elinkList.map((detail) => ({
        id: `elink:${stationId}:${detail.elink_id ?? detail.uuid}`,
        level: 'elink',
        name: detail.elink_name || detail.elink_id,
        elink_id: detail.elink_id ?? detail.uuid,
        dev_id: detail.dev_id,
        station_id: stationId,
        station_name: node.data.station_name,
        powerLimit: detail.powerLimit,
        isLeaf: true,
      }))
      node.data.countText = children.length ? `${children.length}台设备` : '无设备'
      node.data.noDevice = children.length === 0
      resolve(children)
    } catch (e) {
      console.warn('加载电站设备失败', stationId, e)
      resolve([])
    }
    return
  }

  resolve([])
}

/** 搜索：重置并重新加载电站列表 */
async function onSearch() {
  loading.value = true
  try {
    pageNum.value = 1
    await fetchStationsPage(true)
  } finally {
    loading.value = false
  }
}

// 输入即自动搜索（防抖），无需回车
const debouncedSearch = debounce(onSearch, 400)
watch(keyword, () => debouncedSearch())

// 组件挂载时加载首页电站
onSearch()

async function loadMoreStations() {
  loadingMore.value = true
  try {
    pageNum.value += 1
    await fetchStationsPage(false)
  } finally {
    loadingMore.value = false
  }
}

/** elink 详情缓存，避免重复请求同一电站的设备列表 */
const elinkCache = new Map()

async function fetchElinks(stationId, stationName) {
  if (elinkCache.has(stationId)) return elinkCache.get(stationId)
  try {
    const elkRes = await elinks(stationId)
    const list = (elkRes?.data || []).map((detail) => ({
      elink_id: detail.elink_id ?? detail.uuid,
      elink_name: detail.elink_name || detail.elink_id,
      dev_id: detail.dev_id,
      station_id: stationId,
      station_name: stationName,
      powerLimit: detail.powerLimit,
    }))
    elinkCache.set(stationId, list)
    return list
  } catch (e) {
    console.warn('加载电站设备失败', stationId, e)
    return []
  }
}

function mapElinkNode(n) {
  return {
    elink_id: n.elink_id,
    elink_name: n.name,
    dev_id: n.dev_id,
    station_id: n.station_id,
    station_name: n.station_name,
    powerLimit: n.powerLimit,
  }
}

/**
 * 汇总当前勾选的所有 elink 目标。
 * 懒加载场景下，勾选了电站但其子设备尚未展开加载时，getCheckedNodes 不含 elink，
 * 需要对「勾选或半勾选的电站」主动拉取设备补齐，保证统计与下发目标准确。
 */
async function collectCheckedElinks() {
  const checkedNodes = treeRef.value?.getCheckedNodes(false) || []
  const halfChecked = treeRef.value?.getHalfCheckedNodes?.() || []

  // 已加载并勾选的 elink
  const loadedElinks = checkedNodes.filter((n) => n.level === 'elink').map(mapElinkNode)

  // 完全勾选的电站：其全部设备都应纳入（但可能尚未加载子节点）
  const fullyCheckedStations = checkedNodes.filter((n) => n.level === 'station')

  const result = [...loadedElinks]
  const seen = new Set(loadedElinks.map((e) => `${e.station_id}:${e.elink_id}`))

  for (const st of fullyCheckedStations) {
    const list = await fetchElinks(st.station_id, st.station_name)
    for (const e of list) {
      const key = `${e.station_id}:${e.elink_id}`
      if (!seen.has(key)) { seen.add(key); result.push(e) }
    }
  }

  return result
}

async function onCheck() {
  const checked = await collectCheckedElinks()
  const stationSet = new Set(checked.map((c) => c.station_id))
  emit('change', {
    targets: checked,
    stats: { stationCount: stationSet.size, deviceCount: checked.length },
  })
}

function reload() {
  onSearch()
}

/** 清空所有勾选 */
function clearChecked() {
  treeRef.value?.setCheckedKeys([])
  emit('change', { targets: [], stats: { stationCount: 0, deviceCount: 0 } })
}

defineExpose({ reload, clearChecked, getTargets: collectCheckedElinks })
</script>

<style scoped lang="scss">
.target-tree {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}

.target-tree__toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;

  .tt-search {
    flex: 1;
    :deep(.el-input__wrapper) {
      border-radius: 999px;
      box-shadow: 0 0 0 1px rgba(58, 104, 255, 0.12);
      background: #fff;
    }
    :deep(.el-input__wrapper.is-focus) {
      box-shadow: 0 0 0 1px #3A68FF;
    }
  }
}

.target-tree__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 6px;
  border-radius: 12px;
  background: rgba(246, 250, 255, 0.6);
  border: 1px solid rgba(58, 104, 255, 0.08);
}

/* el-tree 整体美化 */
.tt-tree {
  background: transparent;

  :deep(.el-tree-node__content) {
    height: 40px;
    border-radius: 10px;
    margin: 2px 0;
    transition: background 0.18s ease;

    &:hover {
      background: rgba(58, 104, 255, 0.06);
    }
  }

  /* 选中(勾选所在行)高亮 */
  :deep(.el-tree-node.is-checked > .el-tree-node__content) {
    background: rgba(58, 104, 255, 0.08);
  }

  :deep(.el-tree-node__expand-icon) {
    color: rgba(46, 46, 46, 0.4);
    font-size: 14px;
  }

  :deep(.el-checkbox__inner) {
    border-radius: 5px;
    border-color: #C4CCDC;
  }
  :deep(.el-checkbox__input.is-checked .el-checkbox__inner),
  :deep(.el-checkbox__input.is-indeterminate .el-checkbox__inner) {
    background-color: #3A68FF;
    border-color: #3A68FF;
  }

  :deep(.el-loading-spinner .path) { stroke: #3A68FF; }

  /* 无设备的电站行：隐藏勾选框 */
  :deep(.tt-node--no-device > .el-tree-node__content > .el-checkbox) {
    visibility: hidden;
  }
}

.tt-node {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding-right: 8px;
  overflow: hidden;
}

.tt-node__icon {
  flex-shrink: 0;
  font-size: 15px;
}

.tt-node--station {
  .tt-node__icon { color: #3A68FF; }
  .tt-node__label { font-weight: 600; color: #2e2e2e; }
}

.tt-node--elink {
  .tt-node__icon { color: #00A870; }
  .tt-node__label { color: rgba(46, 46, 46, 0.85); }
}

.tt-node__label {
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tt-node__pv {
  flex-shrink: 0;
  margin-left: 6px;
  height: 18px;
  line-height: 16px;
  padding: 0 8px;
}

.tt-node__count {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 11px;
  color: #3A68FF;
  background: rgba(58, 104, 255, 0.1);
  border-radius: 10px;
  padding: 1px 8px;
}

.tt-empty {
  padding: 20px 0;
}

.tt-more {
  text-align: center;
  padding: 8px 0 2px;
}
</style>
